import React from 'react'
import { Link } from 'react-router-dom'
import { useWhatsAppStatus } from '../../contexts/WhatsAppStatusContext'

/**
 * Global red/amber banner shown at the top of every dashboard page whenever
 * the WhatsApp integration is in a bad state. Renders nothing when all is
 * well, so it can live unconditionally in <Layout>.
 *
 * Three cases:
 *   - not_configured : amber informational banner (admins can still work)
 *   - disconnected   : red critical banner (active incident — needs fixing)
 *   - connected      : renders nothing
 *
 * Includes a link to the Integrations page so clicking takes the admin
 * straight to the reconnection controls.
 */
export function WhatsAppDisconnectedBanner() {
  const status = useWhatsAppStatus()

  if (status.state === 'connected' || status.state === 'unknown') return null

  const isCritical = status.state === 'disconnected'
  const bg = isCritical ? 'bg-error-light' : 'bg-warning-light'
  const text = isCritical ? 'text-error' : 'text-warning'
  const dotBg = isCritical ? 'bg-error' : 'bg-warning'

  const title = isCritical
    ? 'WhatsApp is disconnected'
    : 'WhatsApp integration is not configured'

  const detail = isCritical
    ? status.error
      ? `Could not reach the OpenWA gateway (${status.error}). Cached group data is still available.`
      : status.connected
        ? 'OpenWA is reachable but no authenticated WhatsApp session. Scan the QR code on the Integrations page.'
        : 'The OpenWA gateway is not responding. Agents can still see cached group data; new messages will be missed until reconnected.'
    : 'Link a WhatsApp account on the Integrations page to start receiving customer messages.'

  return (
    <div
      role="alert"
      className={`${bg} border-b border-border px-6 py-3 flex items-center gap-3 text-sm`}
    >
      <span
        className={`inline-flex items-center justify-center w-2 h-2 rounded-full ${dotBg} animate-pulse shrink-0`}
        aria-hidden
      />
      <div className="flex-1 min-w-0">
        <span className={`font-semibold ${text}`}>{title}</span>
        <span className={`ml-2 ${text} opacity-90`}>{detail}</span>
      </div>
      <Link
        to="/integrations"
        className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold ${text} bg-surface hover:bg-surface-hover border border-border transition-colors`}
      >
        Fix on Integrations →
      </Link>
    </div>
  )
}
